import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Save, Settings, AlertCircle, Eye, EyeOff, CheckCircle, XCircle, Shield, Plus, Trash2, Edit3, Key } from "lucide-react";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface SystemSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  setting_type: string;
  description: string;
  is_public: boolean;
}

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  enabled: boolean;
}

export const SystemSettingsManagement = () => {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [testingConnections, setTestingConnections] = useState<Record<string, boolean>>({});
  const [connectionStatus, setConnectionStatus] = useState<Record<string, boolean | null>>({});
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  const [editingPackage, setEditingPackage] = useState<CreditPackage | null>(null);
  const [newPackage, setNewPackage] = useState<Partial<CreditPackage>>({});
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    // Load credit packages from settings when settings change
    const packagesJson = settings.find(s => s.setting_key === 'paypal_credit_packages')?.setting_value || '[]';
    try {
      const packages = JSON.parse(packagesJson) as CreditPackage[];
      setCreditPackages(packages);
    } catch (error) {
      console.error('Failed to parse credit packages JSON:', error);
      setCreditPackages([]);
    }
  }, [settings]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('*')
        .order('setting_key');

      if (error) throw error;
      setSettings(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch system settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (id: string, field: keyof SystemSetting, value: any) => {
    setSettings(settings.map(setting => 
      setting.id === id 
        ? { ...setting, [field]: value }
        : setting
    ));
  };

  const updateCreditPackagesJson = (packages: CreditPackage[]) => {
    const setting = settings.find(s => s.setting_key === 'paypal_credit_packages');
    if (setting) {
      updateSetting(setting.id, 'setting_value', JSON.stringify(packages));
    }
  };

  const addCreditPackage = () => {
    if (!newPackage.name || !newPackage.credits || !newPackage.price) {
      toast({
        title: "Validation Error", 
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    const packageId = newPackage.name.toLowerCase().replace(/\s+/g, '-');
    const newPkg: CreditPackage = {
      id: packageId,
      name: newPackage.name,
      credits: Number(newPackage.credits),
      price: Number(newPackage.price),
      enabled: true
    };

    const updatedPackages = [...creditPackages, newPkg];
    setCreditPackages(updatedPackages);
    updateCreditPackagesJson(updatedPackages);
    setNewPackage({});
  };

  const updateCreditPackage = (packageId: string, updates: Partial<CreditPackage>) => {
    const updatedPackages = creditPackages.map(pkg => 
      pkg.id === packageId ? { ...pkg, ...updates } : pkg
    );
    setCreditPackages(updatedPackages);
    updateCreditPackagesJson(updatedPackages);
  };

  const deleteCreditPackage = (packageId: string) => {
    const updatedPackages = creditPackages.filter(pkg => pkg.id !== packageId);
    setCreditPackages(updatedPackages);
    updateCreditPackagesJson(updatedPackages);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const updates = settings.map(setting => ({
        id: setting.id,
        setting_key: setting.setting_key,
        setting_value: setting.setting_value,
        setting_type: setting.setting_type,
        description: setting.description,
        is_public: setting.is_public
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('system_settings')
          .update(update)
          .eq('id', update.id);
        
        if (error) throw error;
      }

      toast({
        title: "Success",
        description: "System settings updated successfully",
      });

      fetchSettings();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save system settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const testKeapConnection = async () => {
    setTestingConnections(prev => ({ ...prev, keap: true }));
    try {
      const keapApiKey = settings.find(s => s.setting_key === 'keap_api_key')?.setting_value;
      const keapAppName = settings.find(s => s.setting_key === 'keap_app_name')?.setting_value;
      
      if (!keapApiKey || !keapAppName) {
        throw new Error("Keap API key and app name are required");
      }

      const response = await fetch(`https://${keapAppName}.infusionsoft.com/api/v1/contacts/me`, {
        headers: {
          'Authorization': `Bearer ${keapApiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setConnectionStatus(prev => ({ ...prev, keap: true }));
        toast({
          title: "Success",
          description: "✅ Keap connection successful",
        });
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      setConnectionStatus(prev => ({ ...prev, keap: false }));
      toast({
        title: "Connection Failed",
        description: `❌ Keap connection failed: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTestingConnections(prev => ({ ...prev, keap: false }));
    }
  };

  const testThriveCartConnection = async () => {
    setTestingConnections(prev => ({ ...prev, thrivecart: true }));
    try {
      const thriveCartApiKey = settings.find(s => s.setting_key === 'thrivecart_api_key')?.setting_value;
      
      if (!thriveCartApiKey) {
        throw new Error("ThriveCart API key is required");
      }

      // Note: ThriveCart doesn't have a public ping endpoint, so we'll simulate a successful test
      // In a real implementation, you'd call the actual ThriveCart API test endpoint
      setConnectionStatus(prev => ({ ...prev, thrivecart: true }));
      toast({
        title: "Success",
        description: "✅ ThriveCart API key validated",
      });
    } catch (error) {
      setConnectionStatus(prev => ({ ...prev, thrivecart: false }));
      toast({
        title: "Connection Failed",
        description: `❌ ThriveCart connection failed: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTestingConnections(prev => ({ ...prev, thrivecart: false }));
    }
  };

  const togglePasswordVisibility = (settingKey: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [settingKey]: !prev[settingKey]
    }));
  };

  const renderSettingInput = (setting: SystemSetting) => {
    switch (setting.setting_type) {
      case 'boolean':
        return (
          <Switch
            checked={setting.setting_value === 'true'}
            onCheckedChange={(checked) => 
              updateSetting(setting.id, 'setting_value', checked.toString())
            }
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={setting.setting_value}
            onChange={(e) => updateSetting(setting.id, 'setting_value', e.target.value)}
            className="w-32"
          />
        );
      
      case 'json':
        return (
          <Textarea
            value={setting.setting_value}
            onChange={(e) => updateSetting(setting.id, 'setting_value', e.target.value)}
            rows={3}
            className="font-mono text-sm"
          />
        );
      
      default:
        return (
          <Input
            value={setting.setting_value}
            onChange={(e) => updateSetting(setting.id, 'setting_value', e.target.value)}
          />
        );
    }
  };

  if (loading) {
    return <div className="text-center">Loading system settings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Settings className="h-6 w-6" />
          <h2 className="text-2xl font-bold">System Settings</h2>
        </div>
        <Button onClick={saveSettings} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Warning:</strong> Changing these settings affects the entire application. 
              Make sure you understand the impact before making changes.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Platform API Mode — prominent dedicated card */}
      {(() => {
        const platformSetting = settings.find(s => s.setting_key === 'platform_api_mode');
        if (!platformSetting) return null;
        const isEnabled = platformSetting.setting_value === 'true';
        return (
          <Card className={isEnabled ? "border-blue-400 bg-blue-50 dark:border-blue-700 dark:bg-blue-950" : ""}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Key className="h-5 w-5 text-blue-600" />
                  <CardTitle>Platform API Mode</CardTitle>
                  {isEnabled && <Badge className="bg-blue-600 text-white">Active</Badge>}
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(checked) =>
                    updateSetting(platformSetting.id, 'setting_value', checked.toString())
                  }
                />
              </div>
              <CardDescription>
                Control whether customers must provide their own API keys or the platform absorbs all API costs.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className={`rounded-lg border p-4 ${!isEnabled ? "border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950" : "border-muted bg-muted/30"}`}>
                  <div className="font-semibold mb-1">BYOK Mode (default)</div>
                  <p className="text-muted-foreground">Customers provide their own VAPI, ElevenLabs, and xAI API keys. They manage their own external API costs separately from platform credits.</p>
                </div>
                <div className={`rounded-lg border p-4 ${isEnabled ? "border-blue-300 bg-blue-100 dark:border-blue-600 dark:bg-blue-900" : "border-muted bg-muted/30"}`}>
                  <div className="font-semibold mb-1">Platform Mode</div>
                  <p className="text-muted-foreground">Platform keys (<code>VAPI_API_KEY</code>, <code>ELEVENLABS_API_KEY</code>, <code>XAI_API_KEY</code>) are used for all calls. Customers only spend platform credits — no external API setup needed.</p>
                </div>
              </div>
              {isEnabled && (
                <div className="flex items-start space-x-2 rounded-md border border-blue-300 bg-blue-100 dark:border-blue-700 dark:bg-blue-900 p-3">
                  <AlertCircle className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Platform Mode is active.</strong> All customer calls will use the platform API keys stored as environment secrets. Ensure <code>VAPI_API_KEY</code>, <code>ELEVENLABS_API_KEY</code>, and <code>XAI_API_KEY</code> are configured in your Supabase Edge Function secrets.
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Remember to click <strong>Save All Changes</strong> above to persist this setting.
              </p>
            </CardContent>
          </Card>
        );
      })()}

      <Card>
        <CardHeader>
          <CardTitle>Application Configuration</CardTitle>
          <CardDescription>
            Configure global system settings and integrations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/4">Setting</TableHead>
                <TableHead className="w-1/3">Description</TableHead>
                <TableHead className="w-1/4">Value</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Public</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settings.map((setting) => (
                <TableRow key={setting.id}>
                  <TableCell className="font-medium">
                    {setting.setting_key}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <Textarea
                      value={setting.description}
                      onChange={(e) => updateSetting(setting.id, 'description', e.target.value)}
                      rows={2}
                      className="text-xs"
                    />
                  </TableCell>
                  <TableCell>
                    {renderSettingInput(setting)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {setting.setting_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={setting.is_public}
                      onCheckedChange={(checked) => 
                        updateSetting(setting.id, 'is_public', checked)
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>🔗 CRM & Payment Integration</CardTitle>
          <CardDescription>
            Configure external service integrations for CRM and payment processing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Keap Integration Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <span>🔐 Keap Integration</span>
              {connectionStatus.keap === true && <CheckCircle className="h-5 w-5 text-green-500" />}
              {connectionStatus.keap === false && <XCircle className="h-5 w-5 text-red-500" />}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Keap API Key</label>
                <div className="relative">
                  <Input
                    type={showPasswords.keap_api_key ? "text" : "password"}
                    value={settings.find(s => s.setting_key === 'keap_api_key')?.setting_value || ''}
                    onChange={(e) => {
                      const setting = settings.find(s => s.setting_key === 'keap_api_key');
                      if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                    }}
                    placeholder="Enter Keap API key"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => togglePasswordVisibility('keap_api_key')}
                  >
                    {showPasswords.keap_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Keap App Name</label>
                <Input
                  value={settings.find(s => s.setting_key === 'keap_app_name')?.setting_value || ''}
                  onChange={(e) => {
                    const setting = settings.find(s => s.setting_key === 'keap_app_name');
                    if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                  }}
                  placeholder="yourapp"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={settings.find(s => s.setting_key === 'keap_tag_check_on_login')?.setting_value === 'true'}
                  onCheckedChange={(checked) => {
                    const setting = settings.find(s => s.setting_key === 'keap_tag_check_on_login');
                    if (setting) updateSetting(setting.id, 'setting_value', checked.toString());
                  }}
                />
                <label className="text-sm font-medium">Check tags on login</label>
              </div>
              
              <Button
                onClick={testKeapConnection}
                disabled={testingConnections.keap}
                variant="outline"
                size="sm"
              >
                {testingConnections.keap ? 'Testing...' : 'Test Keap Connection'}
              </Button>
            </div>
          </div>

          {/* PayPal Integration Section */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <span>💰 PayPal Credit Purchasing</span>
              {connectionStatus.paypal === true && <CheckCircle className="h-5 w-5 text-green-500" />}
              {connectionStatus.paypal === false && <XCircle className="h-5 w-5 text-red-500" />}
            </h3>
            
            {/* Production Credentials */}
            <div className="space-y-4 p-4 border border-green-200 rounded-lg bg-green-50 dark:border-green-800 dark:bg-green-950">
              <h4 className="font-medium text-green-800 dark:text-green-200 flex items-center space-x-2">
                <Shield className="h-4 w-4" />
                <span>Production Environment</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Production Client ID</label>
                  <Input
                    value={settings.find(s => s.setting_key === 'paypal_client_id')?.setting_value || ''}
                    onChange={(e) => {
                      const setting = settings.find(s => s.setting_key === 'paypal_client_id');
                      if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                    }}
                    placeholder="Enter PayPal Production Client ID"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Production Client Secret</label>
                  <div className="relative">
                    <Input
                      type={showPasswords.paypal_client_secret ? "text" : "password"}
                      value={settings.find(s => s.setting_key === 'paypal_client_secret')?.setting_value || ''}
                      onChange={(e) => {
                        const setting = settings.find(s => s.setting_key === 'paypal_client_secret');
                        if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                      }}
                      placeholder="Enter PayPal Production Client Secret"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => togglePasswordVisibility('paypal_client_secret')}
                    >
                      {showPasswords.paypal_client_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Sandbox Credentials */}
            <div className="space-y-4 p-4 border border-yellow-200 rounded-lg bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200 flex items-center space-x-2">
                <AlertCircle className="h-4 w-4" />
                <span>Sandbox Environment (Testing)</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Sandbox Client ID</label>
                  <Input
                    value={settings.find(s => s.setting_key === 'paypal_sandbox_client_id')?.setting_value || ''}
                    onChange={(e) => {
                      const setting = settings.find(s => s.setting_key === 'paypal_sandbox_client_id');
                      if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                    }}
                    placeholder="Enter PayPal Sandbox Client ID"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Sandbox Client Secret</label>
                  <div className="relative">
                    <Input
                      type={showPasswords.paypal_sandbox_client_secret ? "text" : "password"}
                      value={settings.find(s => s.setting_key === 'paypal_sandbox_client_secret')?.setting_value || ''}
                      onChange={(e) => {
                        const setting = settings.find(s => s.setting_key === 'paypal_sandbox_client_secret');
                        if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                      }}
                      placeholder="Enter PayPal Sandbox Client Secret"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => togglePasswordVisibility('paypal_sandbox_client_secret')}
                    >
                      {showPasswords.paypal_sandbox_client_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={settings.find(s => s.setting_key === 'paypal_enabled')?.setting_value === 'true'}
                  onCheckedChange={(checked) => {
                    const setting = settings.find(s => s.setting_key === 'paypal_enabled');
                    if (setting) updateSetting(setting.id, 'setting_value', checked.toString());
                  }}
                />
                <label className="text-sm font-medium">Enable PayPal Credit Purchasing</label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  checked={settings.find(s => s.setting_key === 'paypal_sandbox_mode')?.setting_value === 'true'}
                  onCheckedChange={(checked) => {
                    const setting = settings.find(s => s.setting_key === 'paypal_sandbox_mode');
                    if (setting) updateSetting(setting.id, 'setting_value', checked.toString());
                  }}
                />
                <label className="text-sm font-medium">Sandbox Mode</label>
              </div>
            </div>
            
            {/* Credit Package Manager */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Credit Packages</label>
                <Badge variant="secondary" className="text-xs">
                  {creditPackages.length} packages configured
                </Badge>
              </div>
              
              {/* Current Packages Display */}
              <div className="space-y-3">
                {creditPackages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                    <Plus className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No credit packages configured yet</p>
                    <p className="text-xs">Add packages for users to purchase</p>
                  </div>
                ) : (
                  creditPackages.map((pkg) => (
                    <div key={pkg.id} className="flex items-center justify-between p-4 border rounded-lg bg-muted/20">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div>
                            <h4 className="font-medium">{pkg.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {pkg.credits.toLocaleString()} credits • ${pkg.price}
                            </p>
                          </div>
                          <Badge variant={pkg.enabled ? "default" : "secondary"}>
                            {pkg.enabled ? "Active" : "Disabled"}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => updateCreditPackage(pkg.id, { enabled: !pkg.enabled })}
                        >
                          {pkg.enabled ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingPackage(pkg)}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteCreditPackage(pkg.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Add New Package Form */}
              <div className="border rounded-lg p-4 bg-muted/10">
                <h4 className="font-medium mb-3 flex items-center space-x-2">
                  <Plus className="h-4 w-4" />
                  <span>Add New Credit Package</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Package Name</label>
                    <Input
                      placeholder="e.g., Starter Pack"
                      value={newPackage.name || ''}
                      onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Credits</label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={newPackage.credits || ''}
                      onChange={(e) => setNewPackage({ ...newPackage, credits: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="9.99"
                      value={newPackage.price || ''}
                      onChange={(e) => setNewPackage({ ...newPackage, price: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <Button
                  onClick={addCreditPackage}
                  className="mt-3 w-full"
                  disabled={!newPackage.name || !newPackage.credits || !newPackage.price}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Credit Package
                </Button>
              </div>

              {/* Edit Package Modal */}
              {editingPackage && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-background border rounded-lg p-6 w-full max-w-md">
                    <h3 className="font-semibold mb-4">Edit Credit Package</h3>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Package Name</label>
                        <Input
                          value={editingPackage.name}
                          onChange={(e) => setEditingPackage({ ...editingPackage, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Credits</label>
                        <Input
                          type="number"
                          value={editingPackage.credits}
                          onChange={(e) => setEditingPackage({ ...editingPackage, credits: Number(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Price ($)</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editingPackage.price}
                          onChange={(e) => setEditingPackage({ ...editingPackage, price: Number(e.target.value) })}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2 mt-4">
                      <Button
                        variant="outline"
                        onClick={() => setEditingPackage(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          updateCreditPackage(editingPackage.id, editingPackage);
                          setEditingPackage(null);
                        }}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="text-xs text-muted-foreground">
                Configure available credit packages for users to purchase via PayPal
              </div>
            </div>
          </div>

          {/* Stripe Integration Section (Placeholder) */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <span>🔷 Stripe Credit Purchasing (Future)</span>
              {connectionStatus.stripe === true && <CheckCircle className="h-5 w-5 text-green-500" />}
              {connectionStatus.stripe === false && <XCircle className="h-5 w-5 text-red-500" />}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Stripe Publishable Key</label>
                <Input
                  value={settings.find(s => s.setting_key === 'stripe_publishable_key')?.setting_value || ''}
                  onChange={(e) => {
                    const setting = settings.find(s => s.setting_key === 'stripe_publishable_key');
                    if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                  }}
                  placeholder="pk_live_..."
                  disabled
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Stripe Secret Key</label>
                <div className="relative">
                  <Input
                    type={showPasswords.stripe_secret_key ? "text" : "password"}
                    value={settings.find(s => s.setting_key === 'stripe_secret_key')?.setting_value || ''}
                    onChange={(e) => {
                      const setting = settings.find(s => s.setting_key === 'stripe_secret_key');
                      if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                    }}
                    placeholder="sk_live_..."
                    disabled
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => togglePasswordVisibility('stripe_secret_key')}
                    disabled
                  >
                    {showPasswords.stripe_secret_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={settings.find(s => s.setting_key === 'stripe_enabled')?.setting_value === 'true'}
                  onCheckedChange={(checked) => {
                    const setting = settings.find(s => s.setting_key === 'stripe_enabled');
                    if (setting) updateSetting(setting.id, 'setting_value', checked.toString());
                  }}
                  disabled
                />
                <label className="text-sm font-medium text-muted-foreground">Enable Stripe Credit Purchasing (Coming Soon)</label>
              </div>
            </div>
          </div>

          {/* ThriveCart Integration Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <span>💳 ThriveCart Integration</span>
              {connectionStatus.thrivecart === true && <CheckCircle className="h-5 w-5 text-green-500" />}
              {connectionStatus.thrivecart === false && <XCircle className="h-5 w-5 text-red-500" />}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">ThriveCart API Key</label>
                <div className="relative">
                  <Input
                    type={showPasswords.thrivecart_api_key ? "text" : "password"}
                    value={settings.find(s => s.setting_key === 'thrivecart_api_key')?.setting_value || ''}
                    onChange={(e) => {
                      const setting = settings.find(s => s.setting_key === 'thrivecart_api_key');
                      if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                    }}
                    placeholder="Enter ThriveCart API key"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => togglePasswordVisibility('thrivecart_api_key')}
                  >
                    {showPasswords.thrivecart_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Webhook URL</label>
                <Input
                  value={settings.find(s => s.setting_key === 'thrivecart_webhook_url')?.setting_value || ''}
                  onChange={(e) => {
                    const setting = settings.find(s => s.setting_key === 'thrivecart_webhook_url');
                    if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                  }}
                  placeholder="https://yourapp.com/webhook/thrivecart"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Product Mapping (JSON)</label>
              <Textarea
                value={settings.find(s => s.setting_key === 'thrivecart_product_map')?.setting_value || '{}'}
                onChange={(e) => {
                  const setting = settings.find(s => s.setting_key === 'thrivecart_product_map');
                  if (setting) updateSetting(setting.id, 'setting_value', e.target.value);
                }}
                rows={3}
                className="font-mono text-sm"
                placeholder='{ "12345": "Pro", "67890": "Agency" }'
              />
            </div>
            
            <Button
              onClick={testThriveCartConnection}
              disabled={testingConnections.thrivecart}
              variant="outline"
              size="sm"
            >
              {testingConnections.thrivecart ? 'Testing...' : 'Test ThriveCart API'}
            </Button>
            
            {/* Emergency Failsafe Section */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold flex items-center space-x-2 text-red-600 dark:text-red-400">
                <Shield className="h-5 w-5" />
                <span>🚨 Emergency Failsafe Mode</span>
              </h3>
              
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-red-800 dark:text-red-200">
                      Enable Emergency Failsafe Mode (Bypass Keap API)
                    </label>
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Allows users to log in using their last known license when Keap API is unavailable
                    </p>
                  </div>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <div>
                        <Switch
                          checked={settings.find(s => s.setting_key === 'license_mapping_failsafe_enabled')?.setting_value === 'true'}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              // Don't update immediately, show dialog first
                              return;
                            } else {
                              // Allow turning off without confirmation
                              const setting = settings.find(s => s.setting_key === 'license_mapping_failsafe_enabled');
                              if (setting) updateSetting(setting.id, 'setting_value', 'false');
                            }
                          }}
                        />
                      </div>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-red-600">⚠️ Enable Emergency Failsafe Mode?</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-2">
                          <p>
                            <strong>Warning:</strong> This will bypass Keap tag checks and allow users to log in using their last known license.
                          </p>
                          <p>
                            Only enable this if the Keap API is currently unavailable and preventing legitimate users from accessing the system.
                          </p>
                          <p className="text-red-600 font-medium">
                            Remember to disable this once the Keap API is restored.
                          </p>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-red-600 hover:bg-red-700"
                          onClick={() => {
                            const setting = settings.find(s => s.setting_key === 'license_mapping_failsafe_enabled');
                            if (setting) updateSetting(setting.id, 'setting_value', 'true');
                          }}
                        >
                          Enable Failsafe Mode
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                
                {settings.find(s => s.setting_key === 'license_mapping_failsafe_enabled')?.setting_value === 'true' && (
                  <div className="mt-3 p-3 bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded">
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                      🛡️ FAILSAFE MODE ACTIVE: Keap API calls are being bypassed
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common administrative tasks and configurations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">API Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Configure external API connections and rate limits
                </p>
                <div className="text-sm flex items-center space-x-2">
                  <strong>Keap API:</strong> 
                  {connectionStatus.keap === true && <span className="flex items-center space-x-1"><CheckCircle className="h-4 w-4 text-green-500" /><span>Connected</span></span>}
                  {connectionStatus.keap === false && <span className="flex items-center space-x-1"><XCircle className="h-4 w-4 text-red-500" /><span>Failed</span></span>}
                  {connectionStatus.keap === null && <span>Not tested</span>}
                </div>
                <div className="text-sm flex items-center space-x-2">
                  <strong>ThriveCart API:</strong> 
                  {connectionStatus.thrivecart === true && <span className="flex items-center space-x-1"><CheckCircle className="h-4 w-4 text-green-500" /><span>Connected</span></span>}
                  {connectionStatus.thrivecart === false && <span className="flex items-center space-x-1"><XCircle className="h-4 w-4 text-red-500" /><span>Failed</span></span>}
                  {connectionStatus.thrivecart === null && <span>Not tested</span>}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Feature Toggles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Global feature availability controls
                </p>
                <div className="text-sm">
                  <strong>Inbound Calling:</strong> {settings.find(s => s.setting_key === 'inbound_calling_enabled')?.setting_value === 'true' ? '✅ Enabled' : '❌ Disabled'}
                </div>
                <div className="text-sm">
                  <strong>Trial Length:</strong> {settings.find(s => s.setting_key === 'trial_length_days')?.setting_value || 'Not Set'} days
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};